// components/Footer.jsx
import React from "react";
import {
  Box,
  Typography,
  Link as MLink,
  Stack,
  IconButton,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { useUser } from "../context/UserContext";
import { POLICY_NAV_LABEL } from "../constants/policyLabels";

const SECTION_LABEL_SX = {
  display: "block",
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.8,
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.95)",
  mb: 1.25,
};

const LINK_SX = {
  color: "inherit",
  opacity: 0.85,
  fontSize: 14,
  lineHeight: 1.6,
  transition: "opacity 0.15s ease",
  "&:hover": { opacity: 1, color: "#fff" },
};

function FooterColumn({ title, children }) {
  return (
    <Box>
      <Typography component="h3" sx={SECTION_LABEL_SX}>
        {title}
      </Typography>
      <Stack spacing={1} alignItems="flex-start">
        {children}
      </Stack>
    </Box>
  );
}

function FooterLink({ to, href, children, ...rest }) {
  if (href) {
    return (
      <MLink href={href} underline="hover" sx={LINK_SX} {...rest}>
        {children}
      </MLink>
    );
  }
  return (
    <MLink component={RouterLink} to={to} underline="hover" sx={LINK_SX} {...rest}>
      {children}
    </MLink>
  );
}

export default function Footer({ showWhiteTopBorder = false }) {
  const year = new Date().getFullYear();
  const { user } = useUser();
  const isLoggedIn = Boolean(user);

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: "var(--primary-color)",
        color: "rgba(255,255,255,0.92)",
        alignSelf: "stretch",
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        flexShrink: 0,
        ...(showWhiteTopBorder && { borderTop: "1px solid rgba(255,255,255,0.9)" }),
      }}
    >
      <Box
        sx={{
          width: "100%",
          mx: "auto",
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 5, md: 7 },
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1.6fr 1fr 1fr 1fr",
            },
            columnGap: { xs: 3, md: 6 },
            rowGap: { xs: 4, md: 5 },
            alignItems: "start",
            textAlign: "left",
          }}
        >
          <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" }, maxWidth: { md: 320 } }}>
            <Typography
              variant="h6"
              component={RouterLink}
              to="/"
              sx={{
                display: "inline-block",
                fontWeight: 800,
                color: "inherit",
                textDecoration: "none",
                mb: 1,
                "&:hover": { opacity: 0.95 },
              }}
            >
              CivixThread
            </Typography>
            <Typography
              variant="body2"
              sx={{ opacity: 0.85, lineHeight: 1.6, fontSize: 14, maxWidth: 320 }}
            >
              Navigate the political landscape and engage with real impact.
            </Typography>

            <Stack direction="row" spacing={1.25} sx={{ mt: 2 }}>
              <IconButton
                aria-label="CivixThread on LinkedIn"
                href="https://www.linkedin.com/company/civixthread"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "rgba(255,255,255,0.92)",
                  border: "1px solid rgba(255,255,255,0.22)",
                  borderRadius: 2,
                  p: 1,
                  transition: "background-color 0.15s ease, box-shadow 0.15s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.25)",
                  },
                }}
              >
                <LinkedInIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>

          <FooterColumn title="Explore">
            <FooterLink to="/elections">Election Lookup</FooterLink>
            <FooterLink to="/discover">Discover</FooterLink>
            <FooterLink to="/bills">{POLICY_NAV_LABEL}</FooterLink>
            <FooterLink to="/community">Community</FooterLink>
            <FooterLink to="/events">Events</FooterLink>
          </FooterColumn>

          <FooterColumn title="Product">
            <FooterLink to="/features">Features</FooterLink>
            <FooterLink to="/donate">Donate</FooterLink>
            <FooterLink to="/support">Support</FooterLink>
          </FooterColumn>

          <FooterColumn title="Legal & Contact">
            <FooterLink to="/privacy-policy">Privacy Policy</FooterLink>
            <FooterLink to="/terms#terms-of-service">Terms of Service</FooterLink>
            <FooterLink to="/child-safety-standards">Child Safety Standards</FooterLink>
            <FooterLink href="mailto:support@civixthread.com">
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <EmailOutlinedIcon sx={{ fontSize: 16, opacity: 0.9 }} />
                <Box component="span">support@civixthread.com</Box>
              </Stack>
            </FooterLink>
          </FooterColumn>
        </Box>

        <Box
          sx={{
            mt: { xs: 5, md: 6 },
            pt: { xs: 2.5, md: 3 },
            borderTop: "1px solid rgba(255,255,255,0.14)",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            gap: { xs: 1.5, sm: 0 },
          }}
        >
          <Typography
            variant="caption"
            sx={{ opacity: 0.85, fontSize: 13, textAlign: "center" }}
          >
            © {year} CivixThread. All rights reserved.
          </Typography>

          {!isLoggedIn && (
            <Stack
              direction="row"
              spacing={2}
              divider={
                <Box
                  aria-hidden="true"
                  sx={{
                    width: "1px",
                    bgcolor: "rgba(255,255,255,0.25)",
                    alignSelf: "stretch",
                  }}
                />
              }
              sx={{
                position: { sm: "absolute" },
                right: { sm: 0 },
                top: { sm: "50%" },
                transform: { sm: "translateY(-50%)" },
              }}
            >
              <FooterLink to="/auth/login">Log in</FooterLink>
              <FooterLink to="/auth/signup">Sign up</FooterLink>
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  );
}
